"use client";
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter as useNavigate } from 'next/navigation';

import { Search as SearchIcon, ChevronDown, ChevronRight, Menu, X } from 'lucide-react';
const Header = ({ siteSettings = {}, articles = [], categories = [], pages = [] }) => {
  const navigate = useNavigate();
  const siteName = siteSettings?.seo?.siteTitle || siteSettings?.organization?.name || 'SURGEDOME';
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openSidebarCats, setOpenSidebarCats] = useState({});
  const searchRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('surgedome_current_user'));
      setCurrentUser(user);
    } catch (e) { console.error("Error in Header.jsx:", e); }
  }, []);

  const logout = () => {
    localStorage.removeItem('surgedome_current_user');
    setCurrentUser(null);
    window.location.reload();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return articles.filter(a => {
      if (!a) return false;
      const matchTitle = String(a.title || '').toLowerCase().includes(query);
      const matchCat = String(a.category || '').toLowerCase().includes(query);
      return matchTitle || matchCat;
    }).slice(0, 4);
  };

  const instantResults = getSearchResults();

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      setShowDropdown(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const toggleSidebarCat = (cat) => {
    setOpenSidebarCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const dynamicCategoryStructure = React.useMemo(() => {
    if (!categories || categories.length === 0) return {};
    const struct = {};
    const mains = categories.filter(c => !c.parentId);
    mains.forEach(m => {
      struct[m.name] = categories.filter(c => c.parentId === m.id).map(c => c.name);
    });
    return struct;
  }, [categories]);

  const navHeader = siteSettings?.navigation?.header;
  
  const getNavUrl = (item) => {
    if (item.type === 'category') {
      const cat = categories.find(c => c.id === item.target);
      return cat ? `/category/${cat.slug}` : '#';
    }
    if (item.type === 'page') {
      const page = pages.find(p => p.id === item.target);
      return page ? `/p/${page.slug}` : '#';
    }
    return item.url || '#';
  };

  const getNavLabel = (item) => {
    if (item.label) return item.label;
    if (item.type === 'category') return categories.find(c => c.id === item.target)?.name || 'Category';
    if (item.type === 'page') return pages.find(p => p.id === item.target)?.title || 'Page';
    return 'Link';
  };

  const renderDesktopNav = () => {
    if (navHeader && navHeader.length > 0) {
      return navHeader.sort((a,b) => a.order - b.order).map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        return (
          <li key={item.id} className="relative group flex items-center h-full">
            <Link href={getNavUrl(item)} className="hover:text-[var(--brand-primary)] transition-colors uppercase tracking-wide flex items-center gap-1 h-full py-4">
              {getNavLabel(item)}
              {hasChildren && <ChevronDown className="w-3.5 h-3.5" />}
            </Link>
            {hasChildren && (
              <div className="absolute top-full left-0 bg-brand-surface border border-brand-border rounded shadow-xl invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all z-50 flex flex-col min-w-[160px] py-2">
                {item.children.sort((a,b) => a.order - b.order).map(child => (
                  <Link key={child.id} href={getNavUrl(child)} className="px-4 py-2 hover:bg-[var(--brand-surface-hover)] hover:text-[var(--brand-primary)] transition-colors text-xs font-bold text-brand-text">
                    {getNavLabel(child)}
                  </Link>
                ))}
              </div>
            )}
          </li>
        );
      });
    }
    
    // Fallback
    return Object.entries(dynamicCategoryStructure).map(([mainCat]) => {
      const mainSlug = mainCat.toLowerCase().replace(/ /g, '-');
      return (
        <li key={mainCat} className="relative group flex items-center">
          <Link href={`/category/${mainSlug}`} className="hover:text-[var(--brand-primary)] transition-colors uppercase tracking-wide">
            {mainCat}
          </Link>
        </li>
      );
    });
  };

  const renderMobileNav = () => {
    if (navHeader && navHeader.length > 0) {
      return navHeader.sort((a,b) => a.order - b.order).map((item) => {
        const hasSubCats = item.children && item.children.length > 0;
        const isOpen = openSidebarCats[item.id];
        return (
          <li key={item.id} className="border-b border-brand-border last:border-0">
            <div className="flex justify-between items-center px-6 py-4 hover:bg-[var(--brand-surface-hover)] transition-colors cursor-pointer" onClick={() => hasSubCats ? toggleSidebarCat(item.id) : navigate(getNavUrl(item))}>
              <Link 
                href={getNavUrl(item)} 
                onClick={(e) => { if(hasSubCats) e.preventDefault(); else setIsSidebarOpen(false); }}
                className="text-[17px] text-brand-text"
              >
                {getNavLabel(item)}
              </Link>
              {hasSubCats && (
                <ChevronDown className={`w-5 h-5 text-brand-text transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              )}
            </div>
            {hasSubCats && isOpen && (
              <ul className="bg-brand-surface-alt flex flex-col py-2 border-t border-brand-border">
                {item.children.sort((a,b) => a.order - b.order).map(child => (
                  <li key={child.id}>
                    <Link 
                      href={getNavUrl(child)} 
                      onClick={() => setIsSidebarOpen(false)}
                      className="block px-8 py-3 text-[15px] text-brand-muted hover:text-[var(--brand-text-hover)] hover:bg-[var(--brand-surface-hover)] transition-colors"
                    >
                      {getNavLabel(child)}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      });
    }
    
    // Fallback logic
    return Object.entries(dynamicCategoryStructure).map(([mainCat, subCats]) => {
      const mainSlug = mainCat.toLowerCase().replace(/ /g, '-');
      const hasSubCats = subCats && subCats.length > 0;
      const isOpen = openSidebarCats[mainCat];

      return (
        <li key={mainCat} className="border-b border-brand-border last:border-0">
          <div className="flex justify-between items-center px-6 py-4 hover:bg-[var(--brand-surface-hover)] transition-colors cursor-pointer" onClick={() => hasSubCats ? toggleSidebarCat(mainCat) : navigate(`/category/${mainSlug}`)}>
            <Link 
              href={`/category/${mainSlug}`} 
              onClick={(e) => { if(hasSubCats) e.preventDefault(); else setIsSidebarOpen(false); }}
              className="text-[17px] text-brand-text"
            >
              {mainCat}
            </Link>
            {hasSubCats && (
              <ChevronDown className={`w-5 h-5 text-brand-text transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            )}
          </div>
          
          {hasSubCats && isOpen && (
            <ul className="bg-brand-surface-alt flex flex-col py-2 border-t border-brand-border">
              {subCats.map((subCat) => {
                const subSlug = subCat.toLowerCase().replace(/ /g, '-').replace(/&/g, 'and');
                return (
                  <li key={subCat}>
                    <Link 
                      href={`/category/${mainSlug}/${subSlug}`} 
                      onClick={() => setIsSidebarOpen(false)}
                      className="block px-8 py-3 text-[15px] text-brand-muted hover:text-[var(--brand-text-hover)] hover:bg-[var(--brand-surface-hover)] transition-colors"
                    >
                      {subCat}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    });
  };

  return (
    <>
      <header className="w-full bg-brand-surface z-50 border-b border-brand-border shadow-sm font-sans sticky top-0 transition-colors">
        <div className="max-w-[var(--container-width,1200px)] mx-auto px-4">
          
          {/* Single Row: Logo, Nav, Search, User */}
          <div className="flex justify-between items-center py-4 gap-4">
            
            <div className="flex items-center gap-3">
              <button 
                className="flex items-center justify-center w-10 h-10 text-brand-text bg-transparent rounded-full hover:bg-[var(--brand-background)] transition-colors"
                onClick={() => setIsSidebarOpen(true)}
                aria-label="Open Menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              
              <Link href="/" style={{ fontFamily: "'Audiowide', sans-serif" }} className="text-[24px] md:text-[28px] text-brand-text hover:text-[var(--brand-muted)] transition-colors shrink-0">
                {siteSettings?.organization?.logo ? <img src={siteSettings.organization.logo} alt={siteName} className="h-8 md:h-10 object-contain" /> : siteName}
              </Link>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex flex-1 justify-center h-full">
              <ul className="flex items-center gap-4 lg:gap-6 whitespace-nowrap text-[14px] font-bold text-brand-text h-full">
                {renderDesktopNav()}
              </ul>
            </nav>

            <div className="flex items-center gap-4 shrink-0">
              {/* Search Bar - Small and inline */}
              <div className="relative hidden md:block w-32 lg:w-40 mr-6 lg:mr-10" ref={searchRef}>
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
                <input 
                  type="text" 
                  placeholder="Search..." 
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowDropdown(true);
                  }}
                  onKeyDown={handleSearchSubmit}
                  onFocus={() => { if(searchQuery) setShowDropdown(true); }}
                  className="w-full bg-brand-surface-alt text-brand-text pl-8 pr-3 py-1.5 rounded-full outline-none border border-brand-border focus:border-gray-400 transition-all text-xs"
                />
                
                {showDropdown && searchQuery.trim() !== '' && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-brand-surface border border-brand-border rounded shadow-2xl overflow-hidden z-50">
                    {instantResults.length > 0 ? (
                      <div className="flex flex-col">
                        {instantResults.map(article => (
                          <Link key={article.id} href={`/${article.slug}`} onClick={() => setShowDropdown(false)} className="flex items-center gap-3 p-3 hover:bg-[var(--brand-surface-hover)] border-b border-brand-border last:border-0">
                            <div className="w-8 h-8 flex-shrink-0 bg-gray-200 rounded overflow-hidden">
                              <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[10px] font-bold text-brand-text leading-snug line-clamp-2">{article.title}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 text-center text-xs text-brand-muted">No results found</div>
                    )}
                  </div>
                )}
              </div>

              {/* User Actions - Only show Admin Menu if logged in */}
              {currentUser && (
                <div className="relative group cursor-pointer text-sm font-bold text-brand-text">
                  <span className="flex items-center gap-1">
                    {currentUser.name?.split(' ')[0]} <ChevronDown className="w-4 h-4" />
                  </span>
                  <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <div className="bg-brand-surface border border-brand-border rounded shadow-xl w-32 flex flex-col overflow-hidden">
                      {['super_admin', 'editor', 'author', 'ad_manager'].includes(currentUser.role) && (
                        <Link href={currentUser.role === 'ad_manager' ? '/prudentguide/ads' : '/prudentguide'} className="px-4 py-2 text-xs hover:bg-[var(--brand-surface-hover)]">Admin Panel</Link>
                      )}
                      <button onClick={logout} className="px-4 py-2 text-xs text-left text-red-600 hover:bg-[var(--brand-surface-hover)]">Sign Out</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Overlay */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-[100] flex">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsSidebarOpen(false)}
          ></div>
          
          {/* Sidebar Panel */}
          <div className="relative w-full max-w-sm bg-brand-surface h-full shadow-2xl flex flex-col transform transition-transform duration-300">
            {/* Sidebar Header */}
            <div className="flex justify-between items-center p-6 border-b border-brand-border">
              <Link 
                href="/" 
                onClick={() => setIsSidebarOpen(false)}
                style={{ fontFamily: "'Audiowide', sans-serif" }} 
                className="text-[32px] text-brand-text"
              >
                {siteName}
              </Link>
              <button 
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 text-brand-muted hover:text-[var(--brand-text-hover)] transition-colors rounded-full hover:bg-[var(--brand-surface-hover)]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Sidebar Categories */}
            <div className="flex-1 overflow-y-auto py-4">
              <ul className="flex flex-col">
                {renderMobileNav()}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
